/* ============================================================
   NEO Offer Page — mix-and-match offer card

   A separate file from neo-offer.js on purpose: the mask-only offer
   pages keep running the original untouched. This one only ever looks
   for [data-neo-mix-card], so the two can coexist on one theme.

   Shape of the offer: pick 1, 2 or 3 items from a single list that
   holds every mask colour plus the add-on products. Buy 2 -> 10% off,
   buy 3 -> 20% off, applied at checkout by a discount code so the
   offer stays scoped to this page.
   ============================================================ */

(function () {
  'use strict';

  function markReady(el, key) {
    var attr = 'data-neo-mixready-' + key;
    if (el.hasAttribute(attr)) return false;
    el.setAttribute(attr, '');
    return true;
  }

  function initMixCard(root) {
    if (!markReady(root, 'card')) return;
    var dataEl = root.querySelector('[data-neo-mix-data]');
    if (!dataEl) return;

    var DATA;
    try { DATA = JSON.parse(dataEl.textContent); } catch (e) { return; }

    var items = DATA.items || [];
    var bundles = DATA.bundles || [];
    var sizes = DATA.sizes || [];
    if (!items.length || !bundles.length) return;

    var els = {
      price: root.querySelector('[data-neo-price]'),
      was: root.querySelector('[data-neo-was]'),
      badge: root.querySelector('[data-neo-savebadge]'),
      installment: root.querySelector('[data-neo-installment]'),
      note: root.querySelector('[data-neo-colornote]'),
      colorName: root.querySelector('[data-neo-colorname]'),
      colorRow: root.querySelector('[data-neo-colorrow]'),
      swatches: root.querySelectorAll('[data-neo-mix-swatch]'),
      sizes: root.querySelectorAll('[data-neo-size]'),
      sizeRow: root.querySelector('[data-neo-sizerow]'),
      bundles: root.querySelectorAll('[data-neo-bundle]'),
      cta: root.querySelector('[data-neo-cta]'),
      error: root.querySelector('[data-neo-error]'),
      hh: root.querySelector('[data-neo-hh]'),
      mm: root.querySelector('[data-neo-mm]'),
      ss: root.querySelector('[data-neo-ss]')
    };

    var maxQty = bundles.reduce(function (m, b) { return Math.max(m, b.qty); }, 1);

    var state = {
      size: DATA.defaultSize,
      bundleIndex: DATA.defaultBundleIndex || 0,
      picks: [],
      note: '',
      adding: false,
      added: false
    };
    var lastFeatured;

    /* ---------- item resolution ---------- */

    function itemByKey(key) {
      for (var i = 0; i < items.length; i++) if (items[i].key === key) return items[i];
      return null;
    }

    /* A mask item resolves through the currently selected size; every other
       product has a single variant and ignores size entirely. */
    function resolve(key, size) {
      var it = itemByKey(key);
      if (!it) return null;
      if (it.kind === 'mask') {
        var v = it.variants[size];
        return v ? { id: v.id, price: v.price, available: v.available, item: it } : null;
      }
      return { id: it.variantId, price: it.price, available: it.available, item: it };
    }

    function sellable(key, size) {
      var r = resolve(key, size);
      return !!(r && r.available);
    }

    function firstSellable(size) {
      for (var i = 0; i < items.length; i++) if (sellable(items[i].key, size)) return items[i].key;
      return items[0].key;
    }

    /* The offer is anchored to the mask ("NEO Mask + 1 Item"), so slot 1 leads
       with mask colours and every later slot leads with the kit add-ons. The
       full list stays in every slot, just reordered, so a shopper can still
       build three masks or three grips if that is what she wants. */
    function isAddon(it) { return it.kind !== 'mask'; }

    function itemsForSlot(index) {
      if (index === 0) return items;
      var addons = [], masks = [];
      items.forEach(function (it) { (isAddon(it) ? addons : masks).push(it); });
      return addons.concat(masks);
    }

    function sellableOfKind(size, wantAddon) {
      var out = [];
      for (var i = 0; i < items.length; i++) {
        if (isAddon(items[i]) === wantAddon && sellable(items[i].key, size)) out.push(items[i].key);
      }
      return out;
    }

    /* Slot default matches what the slot leads with, so the pre-selected
       3-item tier really is a mask plus two add-ons, exactly as its label
       says. Each add-on slot takes the next product along, so the default
       kit shows three different things rather than the same grip twice. */
    function defaultForSlot(index, size) {
      if (index === 0) return firstSellable(size);
      var addons = sellableOfKind(size, true);
      if (!addons.length) return firstSellable(size);
      return addons[Math.min(index - 1, addons.length - 1)];
    }

    function unavailableReason(key, size) {
      var it = itemByKey(key);
      var r = resolve(key, size);
      var label = it ? it.label : 'That option';
      if (!r) return label + ' is not made in ' + sizeLabel(size) + '.';
      if (!r.available) {
        return label + (it.kind === 'mask' ? ' is sold out in ' + sizeLabel(size) + '.' : ' is sold out.');
      }
      return label;
    }

    function sizeLabel(value) {
      for (var i = 0; i < sizes.length; i++) if (sizes[i].value === value) return sizes[i].label;
      return value;
    }

    function currentQty() { return bundles[state.bundleIndex].qty; }
    function currentPct() {
      var p = bundles[state.bundleIndex].discount_percent;
      return typeof p === 'number' ? p : 0;
    }

    /* picks stays at maxQty length so stepping 3 -> 2 -> 3 restores slot 3 */
    function normalisePicks() {
      for (var i = 0; i < maxQty; i++) {
        if (!sellable(state.picks[i], state.size)) state.picks[i] = defaultForSlot(i, state.size);
      }
      state.picks.length = maxQty;
    }

    function activePicks() { return state.picks.slice(0, currentQty()); }

    function firstMaskIndex() {
      for (var i = 0; i < currentQty(); i++) {
        var it = itemByKey(state.picks[i]);
        if (it && it.kind === 'mask') return i;
      }
      return -1;
    }

    function showMaskColor(key) {
      var it = itemByKey(key);
      if (!it || it.kind !== 'mask') return;
      var page = root.closest('.neo-page') || document;
      var gallery = page.querySelector('[data-neo-gallery]');
      if (gallery && gallery.neoShowGalleryColor) gallery.neoShowGalleryColor(it.label);
    }

    /* ---------- money ---------- */

    function money(cents) {
      if (typeof cents !== 'number' || isNaN(cents)) return '';
      return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    function subtotalOf(picks) {
      var t = 0;
      for (var i = 0; i < picks.length; i++) {
        var r = resolve(picks[i], state.size);
        if (!r) return null;
        t += r.price;
      }
      return t;
    }

    /* Shopify truncates a percentage discount per unit to the cent, then
       multiplies by quantity. Mirrored exactly or the page and checkout
       disagree by a couple of cents. */
    function discountedOf(picks, pct) {
      var byVariant = {};
      for (var i = 0; i < picks.length; i++) {
        var r = resolve(picks[i], state.size);
        if (!r) return null;
        if (!byVariant[r.id]) byVariant[r.id] = { price: r.price, qty: 0 };
        byVariant[r.id].qty++;
      }
      var total = 0;
      for (var id in byVariant) {
        if (!Object.prototype.hasOwnProperty.call(byVariant, id)) continue;
        var line = byVariant[id];
        var off = Math.floor((line.price * pct / 100) + 1e-6);
        total += (line.price - off) * line.qty;
      }
      return total;
    }

    function previewFor(bundle) {
      var picks = state.picks.slice(0, bundle.qty);
      while (picks.length < bundle.qty) picks.push(firstSellable(state.size));
      var was = subtotalOf(picks);
      if (was === null) return null;
      return { was: was, now: discountedOf(picks, bundle.discount_percent || 0) };
    }

    /* ---------- picker ---------- */

    function closeAllLists(except) {
      Array.prototype.forEach.call(root.querySelectorAll('.neo-pick'), function (f) {
        if (f === except) return;
        f.classList.remove('is-open');
        var t = f.querySelector('.neo-pick-trigger');
        var l = f.querySelector('.neo-pick-list');
        if (t) t.setAttribute('aria-expanded', 'false');
        if (l) l.hidden = true;
      });
    }

    function setThumb(el, url) {
      if (el) el.style.setProperty('--thumb', url ? 'url("' + url + '")' : 'none');
    }

    function optionRow(it, tag) {
      var r = resolve(it.key, state.size);
      var ok = !!(r && r.available);
      var el = document.createElement(tag);
      el.type = 'button';
      el.className = 'neo-pick-opt';
      el.setAttribute('role', 'option');
      el.setAttribute('data-neo-item', it.key);
      el.setAttribute('aria-disabled', ok ? 'false' : 'true');
      /* The image URL is applied via style.setProperty, never interpolated
         into an innerHTML style="" attribute: the quotes a URL needs would
         close the attribute early and the thumbnail silently vanishes. */
      el.innerHTML =
        '<span class="neo-pick-thumb" aria-hidden="true"></span>' +
        '<span class="neo-pick-labels">' +
          '<span class="neo-pick-name"></span>' +
          (it.sub ? '<span class="neo-pick-sub"></span>' : '') +
        '</span>' +
        '<span class="neo-pick-price"></span>';
      setThumb(el.querySelector('.neo-pick-thumb'), it.img);
      el.querySelector('.neo-pick-name').textContent = it.label;
      if (it.sub) el.querySelector('.neo-pick-sub').textContent = it.sub;
      el.querySelector('.neo-pick-price').textContent = r ? money(r.price) : '—';
      return el;
    }

    function buildSlot(index) {
      var field = document.createElement('div');
      field.className = 'neo-pick is-mix';

      var slotLabel = document.createElement('div');
      slotLabel.className = 'neo-slot-label';
      slotLabel.textContent = 'Item ' + (index + 1);

      var trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'neo-pick-trigger';
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.innerHTML =
        '<span class="neo-pick-thumb" aria-hidden="true"></span>' +
        '<span class="neo-sr">Item ' + (index + 1) + ': </span>' +
        '<span class="neo-pick-labels">' +
          '<span class="neo-pick-name" data-neo-slot-name></span>' +
          '<span class="neo-pick-sub" data-neo-slot-sub></span>' +
        '</span>';

      var list = document.createElement('div');
      list.className = 'neo-pick-list';
      list.setAttribute('role', 'listbox');
      list.hidden = true;

      function choose(key) {
        if (!sellable(key, state.size)) {
          state.note = unavailableReason(key, state.size);
          render();
          return;
        }
        state.picks[index] = key;
        state.note = '';
        state.added = false;
        field.classList.remove('is-open');
        list.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
        trigger.focus();
        render();
        showMaskColor(key);
      }

      var lastGroup = null;
      itemsForSlot(index).forEach(function (it) {
        if (it.group && it.group !== lastGroup) {
          lastGroup = it.group;
          var d = document.createElement('div');
          d.className = 'neo-pick-divider';
          d.textContent = it.group;
          list.appendChild(d);
        }
        var opt = optionRow(it, 'button');
        opt.addEventListener('click', function (e) { e.stopPropagation(); choose(it.key); });
        list.appendChild(opt);
      });

      trigger.addEventListener('click', function (e) {
        e.stopPropagation();
        var open = field.classList.contains('is-open');
        closeAllLists();
        if (open) {
          field.classList.remove('is-open'); list.hidden = true;
          trigger.setAttribute('aria-expanded', 'false');
        } else {
          field.classList.add('is-open'); list.hidden = false;
          trigger.setAttribute('aria-expanded', 'true');
          var sel = list.querySelector('[aria-selected="true"]');
          if (sel) sel.focus();
        }
      });

      list.addEventListener('keydown', function (e) {
        var opts = Array.prototype.slice.call(list.querySelectorAll('.neo-pick-opt'));
        var i = opts.indexOf(document.activeElement);
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          var n = e.key === 'ArrowDown' ? i + 1 : i - 1;
          if (n < 0) n = opts.length - 1;
          if (n >= opts.length) n = 0;
          opts[n].focus();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          field.classList.remove('is-open'); list.hidden = true;
          trigger.setAttribute('aria-expanded', 'false'); trigger.focus();
        }
      });

      var wrap = document.createElement('div');
      wrap.className = 'neo-pick is-mix';
      field.appendChild(trigger);
      field.appendChild(list);

      var holder = document.createElement('div');
      holder.style.flex = '1 1 180px';
      holder.style.minWidth = '0';
      holder.appendChild(slotLabel);
      holder.appendChild(field);
      return holder;
    }

    function renderSlots(row) {
      var wrap = row.querySelector('[data-neo-picks]');
      if (!wrap) return;
      wrap.classList.add('is-mix');
      var qty = currentQty();

      if (wrap.childElementCount !== qty || wrap.getAttribute('data-neo-size-key') !== state.size) {
        wrap.innerHTML = '';
        wrap.setAttribute('data-neo-size-key', state.size);
        for (var i = 0; i < qty; i++) wrap.appendChild(buildSlot(i));
      }

      Array.prototype.forEach.call(wrap.children, function (holder, i) {
        var key = state.picks[i];
        var it = itemByKey(key);
        var r = resolve(key, state.size);
        var trigger = holder.querySelector('.neo-pick-trigger');
        setThumb(trigger.querySelector('.neo-pick-thumb'), it ? it.img : '');
        trigger.querySelector('[data-neo-slot-name]').textContent = it ? it.label : '';
        var sub = trigger.querySelector('[data-neo-slot-sub]');
        var subText = it && it.sub ? it.sub : (r ? money(r.price) : '');
        sub.textContent = it && it.sub ? it.sub + ' · ' + money(r ? r.price : 0) : subText;
        Array.prototype.forEach.call(holder.querySelectorAll('.neo-pick-opt'), function (o) {
          var k = o.getAttribute('data-neo-item');
          o.setAttribute('aria-selected', k === key ? 'true' : 'false');
          var rr = resolve(k, state.size);
          o.setAttribute('aria-disabled', rr && rr.available ? 'false' : 'true');
          o.querySelector('.neo-pick-price').textContent = rr ? money(rr.price) : '—';
        });
      });
    }

    /* ---------- render ---------- */

    function render() {
      normalisePicks();

      var qty = currentQty();
      var active = activePicks();
      var was = subtotalOf(active);
      var now = discountedOf(active, currentPct());

      if (els.price) els.price.textContent = now === null ? '—' : money(now);
      if (els.was) {
        var show = was !== null && now !== null && was > now;
        els.was.textContent = show ? money(was) : '';
        els.was.style.display = show ? '' : 'none';
      }
      if (els.badge) {
        var save = (was !== null && now !== null) ? was - now : 0;
        els.badge.textContent = save > 0 ? 'Save ' + money(save) : '';
      }
      if (els.installment && now !== null) els.installment.textContent = money(Math.round(now / 4));
      if (els.note) els.note.textContent = state.note || '';

      // The swatches and first active mask slot share one selection. A kit
      // with only add-ons has no mask controls until a mask is chosen again.
      var maskIndex = firstMaskIndex();
      var featured = maskIndex < 0 ? null : state.picks[maskIndex];
      var featuredItem = itemByKey(featured);
      if (els.colorRow) els.colorRow.hidden = maskIndex < 0;
      if (els.colorName) els.colorName.textContent = featuredItem ? featuredItem.label : '';
      Array.prototype.forEach.call(els.swatches, function (btn) {
        var key = btn.getAttribute('data-neo-mix-swatch');
        var it = itemByKey(key);
        var available = sellable(key, state.size);
        btn.setAttribute('aria-pressed', key === featured ? 'true' : 'false');
        btn.setAttribute('aria-disabled', available ? 'false' : 'true');
        btn.title = available && it ? it.label : unavailableReason(key, state.size);
      });
      // Keep the video on first load, and do not override a gallery thumbnail
      // on an unrelated price/tier repaint.
      if (lastFeatured !== undefined && featured !== lastFeatured) showMaskColor(featured);
      lastFeatured = featured;

      // The size row only matters while a mask is in the mix.
      var anyMask = active.some(function (k) { var it = itemByKey(k); return it && it.kind === 'mask'; });
      if (els.sizeRow) els.sizeRow.style.display = anyMask ? '' : 'none';
      Array.prototype.forEach.call(els.sizes, function (b) {
        b.setAttribute('aria-pressed', b.getAttribute('data-neo-size') === state.size ? 'true' : 'false');
      });

      Array.prototype.forEach.call(els.bundles, function (row, i) {
        var on = i === state.bundleIndex;
        row.classList.toggle('is-on', on);
        row.setAttribute('aria-checked', on ? 'true' : 'false');

        var pv = previewFor(bundles[i]);
        var priceEl = row.querySelector('[data-neo-bundle-price]');
        var wasEl = row.querySelector('[data-neo-bundle-was]');
        var tagEl = row.querySelector('[data-neo-bundle-tag]');
        if (priceEl) priceEl.textContent = pv ? money(pv.now) : '—';
        if (wasEl) {
          var s2 = pv && pv.was > pv.now;
          wasEl.textContent = s2 ? money(pv.was) : '';
          wasEl.style.display = s2 ? '' : 'none';
        }
        if (tagEl) {
          var sv = pv ? pv.was - pv.now : 0;
          tagEl.textContent = sv > 0 ? 'Save ' + money(sv) : '';
          tagEl.style.display = sv > 0 ? '' : 'none';
        }
        if (on) renderSlots(row);
      });

      if (els.cta) {
        if (state.added) {
          els.cta.textContent = DATA.ctaAdded || '✓ Added';
          els.cta.classList.add('is-added'); els.cta.disabled = true;
        } else if (state.adding) {
          els.cta.textContent = DATA.ctaAdding || 'Adding…';
          els.cta.classList.remove('is-added'); els.cta.disabled = true;
        } else {
          els.cta.textContent = DATA.ctaLabel || 'Yes, lock it in';
          els.cta.classList.remove('is-added'); els.cta.disabled = now === null;
        }
      }
    }

    /* ---------- events ---------- */

    Array.prototype.forEach.call(els.swatches, function (btn) {
      btn.addEventListener('click', function () {
        var key = btn.getAttribute('data-neo-mix-swatch');
        if (!sellable(key, state.size)) {
          state.note = unavailableReason(key, state.size);
          render();
          return;
        }
        var index = firstMaskIndex();
        if (index < 0) return;
        state.picks[index] = key;
        state.note = ''; state.added = false;
        closeAllLists();
        render();
        // A click on the already-selected color must also leave the video
        // or add-on photo and reveal that mask.
        showMaskColor(key);
      });
    });

    Array.prototype.forEach.call(els.sizes, function (btn) {
      btn.addEventListener('click', function () {
        state.size = btn.getAttribute('data-neo-size');
        state.note = ''; state.added = false;
        render();
      });
    });

    Array.prototype.forEach.call(els.bundles, function (row, i) {
      row.addEventListener('click', function (e) {
        if (e.target.closest('.neo-pick')) return;
        state.bundleIndex = i; state.added = false; state.note = '';
        render();
      });
      row.addEventListener('keydown', function (e) {
        if (e.target.closest('.neo-pick')) return;
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          state.bundleIndex = i; state.added = false;
          render();
        }
      });
    });

    function outsideClose(e) {
      var t = e.target;
      if (!t || !t.closest || !t.closest('.neo-pick')) closeAllLists();
    }
    document.addEventListener('click', outsideClose);
    document.addEventListener('pointerdown', outsideClose);
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeAllLists(); });

    /* ---------- add to cart ---------- */

    function addToCart() {
      if (state.adding) return;
      var active = activePicks();
      var wanted = {};
      var bad = null;

      for (var i = 0; i < active.length; i++) {
        var r = resolve(active[i], state.size);
        if (!r || !r.available) { bad = active[i]; break; }
        wanted[r.id] = (wanted[r.id] || 0) + 1;
      }
      if (bad) {
        if (els.error) els.error.textContent = unavailableReason(bad, state.size);
        return;
      }

      /* Absolute quantities across every variant this page can sell, so the
         CTA is idempotent: reaching checkout, pressing Back and clicking
         again leaves exactly the chosen bundle, not double. Anything else in
         the cart is left alone. */
      var updates = {};
      (DATA.allVariantIds || []).forEach(function (id) { updates[id] = 0; });
      for (var vid in wanted) {
        if (Object.prototype.hasOwnProperty.call(wanted, vid)) updates[vid] = wanted[vid];
      }

      if (els.error) els.error.textContent = '';
      state.adding = true;
      render();

      clearTimeout(addToCart._t);
      addToCart._t = setTimeout(function () {
        if (state.adding) { state.adding = false; render(); }
      }, 12000);

      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify({ updates: updates })
      })
        .then(function (r) {
          return r.json().then(function (body) {
            if (!r.ok) throw new Error(body.description || body.message || 'Could not update the cart');
            return body;
          });
        })
        .then(function () {
          var code = (bundles[state.bundleIndex] || {}).code || '';
          if (DATA.afterAdd === 'checkout') {
            if (els.cta) els.cta.textContent = DATA.ctaCheckout || 'Taking you to checkout…';
            window.location.href = code ? '/checkout?discount=' + encodeURIComponent(code) : '/checkout';
            return;
          }
          if (code) fetch('/discount/' + encodeURIComponent(code)).catch(function () {});
          state.adding = false; state.added = true; render();
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'neo-mix-card' });
          }
          setTimeout(function () { if (state.added) { state.added = false; render(); } }, 1800);
        })
        .catch(function (err) {
          state.adding = false; state.added = false; render();
          if (els.error) els.error.textContent = (err && err.message) || 'We could not add that. Please try again.';
        });
    }

    if (els.cta) els.cta.addEventListener('click', addToCart);

    // Back from checkout restores this page frozen out of the bfcache.
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted) return;
      clearTimeout(addToCart._t);
      state.adding = false; state.added = false;
      render();
    });
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible' && state.adding) {
        clearTimeout(addToCart._t);
        state.adding = false; render();
      }
    });

    /* ---------- countdown ---------- */

    if (DATA.countdownSeconds > 0 && els.hh) {
      var KEY = 'neo-mix-countdown-' + (DATA.sectionId || 'default');
      var endsAt;
      try { var s = window.sessionStorage.getItem(KEY); endsAt = s ? parseInt(s, 10) : 0; } catch (e) { endsAt = 0; }
      var nowMs = Date.now();
      if (!endsAt || endsAt < nowMs) {
        endsAt = nowMs + DATA.countdownSeconds * 1000;
        try { window.sessionStorage.setItem(KEY, String(endsAt)); } catch (e) {}
      }
      var pad = function (n) { return String(n).padStart(2, '0'); };
      var tick = function () {
        var left = Math.max(0, Math.floor((endsAt - Date.now()) / 1000));
        els.hh.textContent = pad(Math.floor(left / 3600));
        els.mm.textContent = pad(Math.floor((left % 3600) / 60));
        els.ss.textContent = pad(left % 60);
        if (left <= 0 && timer) clearInterval(timer);
      };
      var timer = setInterval(tick, 1000);
      tick();
    }

    state.picks = [];
    for (var seed = 0; seed < maxQty; seed++) {
      state.picks.push(seed === 0
        ? (DATA.defaultItem || items[0].key)
        : defaultForSlot(seed, state.size));
    }
    render();
  }

  function boot(scope) {
    var r = scope || document;
    Array.prototype.forEach.call(r.querySelectorAll('[data-neo-mix-card]'), initMixCard);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else { boot(); }

  if (!window.__neoMixListeners) {
    window.__neoMixListeners = true;
    document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  }
})();
